import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo, { orgSchema, websiteSchema, faqSchema } from "@/components/Seo";
import { api } from "@/lib/api";
import { ShieldCheck, Heart, Star, Search, CalendarCheck, Languages, Users, FileCheck, MessageCircle, Sparkles, ChevronDown } from "lucide-react";

const FAQ = [
  {q: "How do you verify caregivers?", a: "Every caregiver submits a police background check and CPR certification. Our team hand-reviews each application, and only cleared profiles receive the gold Verified seal."},
  {q: "What can I do on the free plan?", a: "Free accounts let you browse a curated selection of caregivers, view their verifications, languages and reviews. To message caregivers, request bookings or save favourites, upgrade to Premium."},
  {q: "How much is Premium?", a: "$9.99 per month. Unlimited search, unlimited messaging, saved favourites, bookings, AI matching, availability calendar, priority support. Cancel any time."},
  {q: "Do caregivers pay to join?", a: "Never. Caregivers join, verify and receive booking requests completely free — we only charge families for Premium access."},
  {q: "Is my data safe?", a: "Yes. Verifications and personal notes are stored securely, cookies are httpOnly, and messages stay between you and your caregiver."},
  {q: "Can I cancel my Premium at any time?", a: "Of course. You keep Premium access until the end of your billing period, then automatically return to Free — no lock-in."},
];

const TESTIMONIALS = [
  {q: "We didn't just find a sitter — we found calm. The whole experience felt considered.", by: "An early Sakeenah family"},
  {q: "The verification badges gave me real peace of mind before our first booking.", by: "Mother of two, Toronto"},
  {q: "The multilingual sitters here are what set Sakeenah apart. Warm, aware, and reliable.", by: "New parent, Montreal"},
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-linen text-forest">
      <Seo
        title="Find trusted, verified caregivers near you"
        description="Sakeenah Circle is a premium membership platform connecting families with verified, CPR-certified caregivers. Search by postal code, language, availability and reviews — book with confidence."
        path="/"
        jsonLd={[orgSchema(), websiteSchema(), faqSchema(FAQ)]}
      />
      <Nav />
      <Hero />
      <TrustStrip />
      <HowItWorksSection />
      <WhyTrustSection />
      <VerificationSection />
      <CitiesSection />
      <TestimonialsSection />
      <PricingTeaser />
      <FaqSection />
      <FinalCta />
      <Footer />
    </div>
  );
}

function CitiesSection() {
  const [cities, setCities] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/seo/cities"); setCities(data.slice(0, 8)); } catch (_) {} })(); }, []);
  if (!cities.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" aria-label="Caregivers by city">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="badge-sage">Local caregivers</span>
        <h2 className="font-serif text-4xl sm:text-5xl mt-3 tracking-tight text-forest leading-tight">Warm hands, near you.</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cities.map((c) => (
          <Link key={c.slug} to={`/babysitters/${c.slug}`} className="card-organic p-5 hover:border-forest transition-colors" data-testid={`city-${c.slug}`}>
            <div className="text-xs uppercase tracking-widest text-forest/60">Caregivers</div>
            <div className="font-serif text-2xl text-forest mt-1">{c.city}</div>
            <div className="text-xs text-forest/60 mt-2">{c.count} verified · ★ {c.rating_avg || "New"}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 space-y-7 animate-fade-up">
          <span className="badge-sage" data-testid="hero-tagline">A calmer way to find childcare</span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight text-forest">
            Find <em className="text-gold not-italic">trusted, verified</em> caregivers near you.
          </h1>
          <p className="text-base sm:text-lg text-forest/75 leading-relaxed max-w-xl">
            Sakeenah Circle is a premium membership platform connecting families with vetted, CPR-certified caregivers. Search by postal code, language and availability — request bookings with confidence.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" className="btn-primary" data-testid="hero-find-btn">Get started free</Link>
            <Link to="/pricing" className="btn-outline" data-testid="hero-pricing-btn">See Premium</Link>
          </div>
          <div className="flex items-center gap-5 pt-4 text-sm text-forest/70 flex-wrap">
            <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-gold" /> Police-checked</div>
            <div className="flex items-center gap-1.5"><Heart className="w-4 h-4 text-gold" /> CPR-certified</div>
            <div className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-gold" /> AI matching</div>
          </div>
        </div>
        <div className="md:col-span-5 relative">
          <div className="card-organic p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-sage/25 blur-3xl" />
            <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-gold/20 blur-3xl" />
            <div className="relative space-y-5">
              <div className="flex items-center gap-2 text-gold">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" />)}
              </div>
              <p className="font-serif text-xl md:text-2xl text-forest leading-snug">"Every caregiver we met on Sakeenah felt hand-picked. The peace of mind is worth the membership on its own."</p>
              <div className="text-sm text-forest/60">— A founding Sakeenah family</div>
              <div className="pt-4 border-t border-taupe grid grid-cols-3 gap-3 text-center">
                <div><div className="font-serif text-xl text-forest">100%</div><div className="text-[10px] uppercase tracking-widest text-forest/60 mt-1">Police-checked</div></div>
                <div><div className="font-serif text-xl text-forest">CPR</div><div className="text-[10px] uppercase tracking-widest text-forest/60 mt-1">Certified</div></div>
                <div><div className="font-serif text-xl text-forest">10+</div><div className="text-[10px] uppercase tracking-widest text-forest/60 mt-1">Languages</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="border-y border-taupe/60 bg-taupe/20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
        {[["Verified", "Police + CPR"], ["Languages", "10+ spoken"], ["Response", "Under 2 hrs"], ["Reviews", "Family-first"]].map((it) => (
          <div key={it[0]}>
            <div className="text-[11px] tracking-[0.15em] uppercase text-forest/60">{it[0]}</div>
            <div className="font-serif text-xl text-forest mt-1">{it[1]}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { icon: Search, t: "Search with intention", d: "Filter by postal code, CPR/police checks, languages, availability and rating.", n: "01" },
    { icon: MessageCircle, t: "Message & get matched", d: "Chat directly with caregivers, or let our AI matcher surface your best fits.", n: "02" },
    { icon: CalendarCheck, t: "Book with confidence", d: "Send a booking request. Your caregiver accepts within hours — you're set.", n: "03" },
  ];
  return (
    <section id="how" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
      <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
        <div className="md:col-span-7">
          <span className="badge-sage">How it works</span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-3 tracking-tight text-forest leading-tight">Trusted childcare, in three quiet steps.</h2>
        </div>
        <p className="md:col-span-5 text-forest/70 leading-relaxed">Three intentional steps between you and the right caregiver — no noise, no overwhelm.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {steps.map((s, i) => (
          <div key={i} className="card-organic p-8" data-testid={`step-${i + 1}`}>
            <div className="flex items-start justify-between">
              <s.icon className="w-8 h-8 text-forest" />
              <span className="font-serif text-3xl text-taupe">{s.n}</span>
            </div>
            <h3 className="font-serif text-2xl mt-6 text-forest">{s.t}</h3>
            <p className="text-sm text-forest/70 mt-2 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhyTrustSection() {
  const items = [
    { icon: ShieldCheck, t: "Vetted, verified", d: "Every caregiver is police-checked, CPR-certified, and hand-reviewed by our team." },
    { icon: Users, t: "Culturally aware", d: "Multilingual caregivers who understand the values and rhythm of your home." },
    { icon: Sparkles, t: "AI matching", d: "Premium members get intelligent caregiver matches based on their family's unique needs." },
    { icon: Heart, t: "Genuine reviews", d: "Real, moderated family reviews — no bots, no bought praise." },
  ];
  return (
    <section id="why" className="bg-taupe/25 border-y border-taupe/50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="badge-sage">Why families trust us</span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-3 tracking-tight text-forest leading-tight">A circle you'd be proud to invite into your home.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {items.map((it, i) => (
            <div key={i} className="card-organic p-6" data-testid={`trust-${i}`}>
              <div className="w-12 h-12 rounded-full bg-sage/30 flex items-center justify-center">
                <it.icon className="w-6 h-6 text-forest" />
              </div>
              <h3 className="font-serif text-xl mt-4 text-forest">{it.t}</h3>
              <p className="text-sm text-forest/70 mt-2 leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VerificationSection() {
  const checks = [
    { icon: FileCheck, t: "Police background check", d: "Documented, recent and reviewed by hand." },
    { icon: Heart, t: "CPR & first-aid certification", d: "Uploaded certificate verified against issuing body." },
    { icon: Users, t: "Reference calls", d: "We speak with at least two previous families before approval." },
    { icon: ShieldCheck, t: "In-person interview", d: "A short conversation to feel out warmth and professionalism." },
  ];
  return (
    <section id="verification" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-12 gap-10 items-start">
      <div className="md:col-span-5">
        <span className="badge-gold">Caregiver verification</span>
        <h2 className="font-serif text-4xl sm:text-5xl mt-3 tracking-tight text-forest leading-tight">Every caregiver, thoughtfully vetted.</h2>
        <p className="text-forest/75 mt-4 leading-relaxed">Sakeenah caregivers pass a 4-step verification before they ever meet your family. It's slower — and worth it.</p>
        <Link to="/browse" className="btn-primary inline-block mt-6" data-testid="verify-cta">Browse verified caregivers</Link>
      </div>
      <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
        {checks.map((c, i) => (
          <div key={i} className="card-organic p-6 flex gap-4 items-start">
            <div className="w-11 h-11 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
              <c.icon className="w-5 h-5 text-gold" />
            </div>
            <div>
              <h4 className="font-serif text-lg text-forest">{c.t}</h4>
              <p className="text-sm text-forest/70 mt-1 leading-relaxed">{c.d}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="bg-taupe/25 border-y border-taupe/50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="badge-sage">Parent testimonials</span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-3 tracking-tight text-forest leading-tight">Care, from the families we've helped.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-14">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="card-organic p-7" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 text-gold">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-gold text-gold" />)}
              </div>
              <p className="font-serif text-lg text-forest mt-4 leading-relaxed">"{t.q}"</p>
              <div className="text-sm text-forest/60 mt-4">— {t.by}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingTeaser() {
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
      <div className="text-center max-w-2xl mx-auto">
        <span className="badge-gold">Pricing</span>
        <h2 className="font-serif text-4xl sm:text-5xl mt-3 tracking-tight text-forest leading-tight">Free for caregivers. Premium for parents.</h2>
        <p className="text-forest/70 mt-4">One membership, everything you need to book care with grace.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6 mt-14 max-w-4xl mx-auto">
        <div className="card-organic p-8" data-testid="plan-free">
          <div className="text-xs uppercase tracking-[0.15em] text-forest/60">Parent · Free</div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-serif text-5xl text-forest">$0</span>
            <span className="text-sm text-forest/60">/ forever</span>
          </div>
          <p className="text-forest/70 mt-4 text-sm">Start exploring the circle.</p>
          <ul className="mt-6 space-y-2.5 text-sm text-forest/80">
            <li>✓ Free parent account</li>
            <li>✓ Browse a curated selection of caregivers</li>
            <li>✓ See verifications, languages & reviews</li>
            <li className="text-forest/40">— Messaging, bookings & AI matching require Premium</li>
          </ul>
          <Link to="/register" className="btn-outline w-full block text-center mt-6" data-testid="plan-free-cta">Get free account</Link>
        </div>
        <div className="card-organic p-8 border-forest/60 relative" data-testid="plan-premium">
          <span className="absolute top-4 right-4 badge-gold"><Sparkles className="w-3 h-3" /> Most popular</span>
          <div className="text-xs uppercase tracking-[0.15em] text-forest/60">Parent · Premium</div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-serif text-5xl text-forest">$9.99</span>
            <span className="text-sm text-forest/60">/ month</span>
          </div>
          <p className="text-forest/70 mt-4 text-sm">Unlock the full Sakeenah Circle.</p>
          <ul className="mt-6 space-y-2.5 text-sm text-forest/80">
            <li>✓ Unlimited caregiver search</li>
            <li>✓ Unlimited direct messaging</li>
            <li>✓ Save favourite caregivers</li>
            <li>✓ Booking requests & availability calendar</li>
            <li>✓ AI-powered caregiver matching</li>
            <li>✓ Priority support</li>
          </ul>
          <Link to="/pricing" className="btn-primary w-full block text-center mt-6" data-testid="plan-premium-cta">See Premium</Link>
          <div className="text-[11px] text-forest/50 text-center mt-3">Cancel any time · No lock-in</div>
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="bg-taupe/25 border-y border-taupe/50">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20">
        <div className="text-center">
          <span className="badge-sage">FAQ</span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-3 tracking-tight text-forest leading-tight">Questions, gently answered.</h2>
        </div>
        <div className="mt-12 space-y-3">
          {FAQ.map((f, i) => (
            <button
              key={i}
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full card-organic p-6 text-left"
              data-testid={`faq-${i}`}
            >
              <div className="flex items-center justify-between gap-4">
                <span className="font-serif text-lg text-forest">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-forest/60 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </div>
              {open === i && <p className="text-sm text-forest/75 leading-relaxed mt-4">{f.a}</p>}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
      <div className="card-organic p-10 md:p-14 text-center relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-sage/30 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-gold/20 blur-3xl" />
        <div className="relative">
          <h2 className="font-serif text-4xl sm:text-5xl text-forest">Ready for a calmer way to find care?</h2>
          <p className="text-forest/75 mt-4 max-w-xl mx-auto">Create a free account in under a minute. Upgrade whenever you're ready.</p>
          <div className="flex justify-center gap-3 mt-8 flex-wrap">
            <Link to="/register" className="btn-primary" data-testid="final-register">Get started free</Link>
            <Link to="/register?role=babysitter" className="btn-outline" data-testid="final-become">Become a caregiver</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
